import {Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {AdvancedSearchUtils} from '@application/helper/advanced-search-utils';
import {DrawingLibraryViewMode} from '@domain/drawing-library/drawing-library-view-mode.enum';
import {Drawing} from '@domain/production-schedule/drawing';
import {PropertyValue} from '@domain/property-value';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {BaseComponent} from '@vdw/angular-component-library';
import {filter, find, isEmpty, isEqual, isNil, reduce, size, some} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-drawing-library-thumbnails',
  templateUrl: './drawing-library-thumbnails.component.html',
  styleUrls: ['./drawing-library-thumbnails.component.scss']
})
export class DrawingLibraryThumbnailsComponent extends BaseComponent implements OnChanges {
  @Input() public advancedSearchFilters: PropertyValue[] = [];
  @Input() public viewMode: DrawingLibraryViewMode;
  @Input() public allowSelectMultipleDrawings: boolean;
  @Input() public selectedDrawingId: number;
  @Output() public drawingSelected: EventEmitter<ListDrawingConfiguration[]> = new EventEmitter<ListDrawingConfiguration[]>();

  public listOfDrawings: ListDrawingConfiguration[] = [];
  public selectedDrawings: ListDrawingConfiguration[] = [];

  private drawingsHaveBeenLoaded = false;
  private canGetMoreDrawings = false;
  private uniqueDrawingsCount = 0;
  private readonly SLICE_LENGTH = 150;

  public constructor(@Inject(DRAWINGS) private readonly drawings: Drawings) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!isNil(changes.viewMode) && !isEqual(changes.viewMode.currentValue, changes.viewMode.previousValue)) {
      this.reloadDrawings();
    }
  }

  public onDrawingSelected(drawingConfigurations: ListDrawingConfiguration[]): void {
    this.selectedDrawings = drawingConfigurations;
    this.drawingSelected.emit(drawingConfigurations);
  }

  public onDrawingDeleted(deletedDrawings: Drawing[]): void {
    this.listOfDrawings = filter(
      this.listOfDrawings,
      (drawingConfiguration: ListDrawingConfiguration) => !some(deletedDrawings, (deletedDrawing: Drawing) => isEqual(deletedDrawing.id, drawingConfiguration.drawing.id))
    );

    this.selectedDrawings = [];
  }

  public onDrawingCanceled(): void {
    this.selectedDrawings = [];
  }

  public onDrawingUpdated(updatedDrawing: Drawing): void {
    let drawingWrappers: ListDrawingConfiguration[] = [];

    if (this.isModeThumbnailsWithConfigurations()) {
      drawingWrappers = ListDrawingConfiguration.getDrawingsForAllConfigurations([updatedDrawing]);
    } else {
      drawingWrappers = [new ListDrawingConfiguration(updatedDrawing, 0)];
    }

    this.listOfDrawings = reduce(
      this.listOfDrawings,
      (newListOfDrawings: ListDrawingConfiguration[], drawingConfiguration: ListDrawingConfiguration) => {
        if (isEqual(updatedDrawing.id, drawingConfiguration.drawing.id)) {
          drawingConfiguration = find(drawingWrappers, (drawingWrapper: ListDrawingConfiguration) => isEqual(drawingConfiguration.configurationIndex, drawingWrapper.configurationIndex));
        }

        if (!isNil(drawingConfiguration)) {
          newListOfDrawings.push(drawingConfiguration);
        }

        return newListOfDrawings;
      },
      []
    );
  }

  public hasDrawings(): boolean {
    return !isEmpty(this.listOfDrawings);
  }

  public canShowProgressSpinner(): boolean {
    return !this.drawingsHaveBeenLoaded;
  }

  public reloadDrawings(advancedSearchFilters?: PropertyValue[]): void {
    if (!isNil(advancedSearchFilters)) {
      this.advancedSearchFilters = advancedSearchFilters;
    }
    this.resetDrawingsList();
    this.getDrawings();
  }

  public get hasFilterActive(): boolean {
    for (const searchFilter of this.advancedSearchFilters) {
      if (AdvancedSearchUtils.hasValue(searchFilter)) {
        return true;
      }
    }
    return false;
  }

  public onVirtualScrollerEnd(): void {
    if (this.canGetMoreDrawings) {
      this.canGetMoreDrawings = false;
      this.getDrawings();
    }
  }

  private resetDrawingsList(): void {
    this.listOfDrawings = [];
    this.selectedDrawings = [];
    this.drawingsHaveBeenLoaded = false;
    this.uniqueDrawingsCount = 0;
  }

  private getDrawings(): void {
    this.drawings
      .getSlice(this.uniqueDrawingsCount, this.SLICE_LENGTH, 'thumbs', this.isModeThumbnailsWithConfigurations(), TargetForListOfDrawingsEnum.DRAWING_LIBRARY, this.advancedSearchFilters)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((drawings: Drawing[]) => {
        let drawingWrappers: ListDrawingConfiguration[] = [];
        if (this.isModeThumbnailsWithConfigurations()) {
          drawingWrappers = ListDrawingConfiguration.getDrawingsForAllConfigurations(drawings);
        } else {
          drawingWrappers = drawings.map((drawing: Drawing) => {
            return new ListDrawingConfiguration(drawing, 0);
          });
        }
        this.listOfDrawings.push(...drawingWrappers);

        if (this.selectedDrawingId) {
          this.selectedDrawings = [this.listOfDrawings.find((drawing: ListDrawingConfiguration) => this.selectedDrawingId === drawing.drawing.id)];
        }

        const drawingsAmount = size(drawings);

        this.uniqueDrawingsCount += drawingsAmount;
        this.canGetMoreDrawings = isEqual(drawingsAmount, this.SLICE_LENGTH);

        this.drawingsHaveBeenLoaded = true;
      });
  }

  private isModeThumbnailsWithConfigurations(): boolean {
    return isEqual(this.viewMode, DrawingLibraryViewMode.THUMBNAILS_WITH_CONFIGURATIONS);
  }
}
